import React, { useEffect, useState } from 'react';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { MAIN_STORE_ROUTE_KEYS } from 'utils/constants';
import './styles.scss';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { BatchesType } from 'models/batches.model';
import { handleApiError } from 'utils/functions';
import WasteForm, { DefaultBatchType, WastePayloadType } from 'components/base/waste-form';
import useDashboardContext from 'contexts/dashboard.context';
import { useParams } from 'react-router-dom';
import useRoleContext from 'contexts/role.context';
import { InventoryModule, RolePermission } from 'models/permission.model';

const KitchenInventoryStockEdit = () => {
  const { activeStore } = useDashboardContext();
  const { can } = useRoleContext();
  const canWriteInventoryManagement = can(
    InventoryModule.InventoryManagement,
    RolePermission.Write
  );
  const { stock_id } = useParams();
  const [isRecordingWastage, setIsRecordingWastage] = useState(false);
  const [fetchedBatches, setFetchedBatches] = useState<BatchesType[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const router = useNavigate();
  const [ingredientDetails, setIngredientDetails] = useState<{
    name: string;
    unitType: string;
  } | null>(null);
  const [defaultBatch, setDefaultBatch] = useState<DefaultBatchType>({
    batch_no: 0,
    id: ''
  });

  if (!activeStore || !stock_id) {
    return null;
  }

  const stockId = stock_id.split('_')[0];
  const batchNo = Number(stock_id.split('_')[1]);

  const fetchBatches = async () => {
    if (!stockId || !activeStore.id) return;
    setIsLoading(true);

    try {
      const response = await apiService.get(`ingredients/${stockId}/stores/${activeStore.id}`, {
        version: 'v2'
      });
      if (response.data.data) {
        const batches = response.data.data.kitchen_batches || [];
        const matchedBatch = batches.find((batch: BatchesType) => batch.batch_no === batchNo);

        if (matchedBatch) {
          setDefaultBatch({
            batch_no: matchedBatch.batch_no,
            id: matchedBatch.id
          });
        }
        setFetchedBatches(batches);
        setIngredientDetails({
          name: response.data.data.name,
          unitType: response.data.data.unit_type
        });
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  const recordWastage = async (data: WastePayloadType) => {
    if (!canWriteInventoryManagement) return;
    if (!stock_id || !activeStore.id) return;
    try {
      setIsRecordingWastage(true);

      const wastageData = {
        ingredient_id: stockId,
        store_id: activeStore.id,
        ...data
      };
      const response = await apiService.post('kitchen-store/record-wastage', {
        version: 'v2',
        data: wastageData
      });
      if (response) {
        toast(<ToastMessage message='Wastage recorded successfully' type='success' />);
        router(MAIN_STORE_ROUTE_KEYS.KITCHEN_INVENTORY_STOCK);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsRecordingWastage(false);
    }
  };

  return (
    <>
      {isLoading || !activeStore.id ? (
        <div className='mt-12 flex justify-center items-center'>
          <p>Loading...</p>
        </div>
      ) : fetchedBatches.length === 0 ? (
        <div className='mt-6 flex justify-center items-center'>
          <p> No batches are available</p>
        </div>
      ) : (
        ingredientDetails && (
          <WasteForm
            ingredientDetails={ingredientDetails}
            submitting={isRecordingWastage}
            onSubmit={recordWastage}
            batches={fetchedBatches}
            onCancel={() => router(-1)}
            defaultBatch={defaultBatch}
            canWrite={canWriteInventoryManagement}
          />
        )
      )}
    </>
  );
};

export default KitchenInventoryStockEdit;
